import type {
  NotificationRuleView,
  NotificationSettingsView,
  QuietHoursView,
  TemplateListView,
  TemplatePreviewRequest,
  TemplatePreviewView,
  TemplateSaveRequest,
  TemplateTestRequest,
  TemplateTestView,
  TemplateVersionsView,
  TemplateView,
  UpdateNotificationRuleRequest,
  UpdateQuietHoursRequest,
} from "@planerius/contracts/cabinet-notifications"

import { apiRequest } from "./http"

/**
 * Шаблоны сообщений (4-Б6).
 *
 * ШАБЛОН ОТКРЫВАЕТСЯ ПО ПАРЕ «СОБЫТИЕ × КАНАЛ», а не по идентификатору строки: пока
 * салон текст не правил, строки в базе нет вовсе, и требовать её значило бы запретить
 * открывать наш текст на правку. Версии и откат, наоборот, ходят по идентификатору —
 * без правок версий не бывает.
 */

export async function fetchTemplates(signal?: AbortSignal): Promise<TemplateListView> {
  return apiRequest<TemplateListView>("/cabinet/notifications/templates", { signal })
}

export async function fetchTemplate(
  event: string,
  channel: string,
  signal?: AbortSignal,
): Promise<TemplateView> {
  return apiRequest<TemplateView>(
    `/cabinet/notifications/templates/${encodeURIComponent(event)}/${encodeURIComponent(channel)}`,
    { signal },
  )
}

export async function saveTemplate(
  event: string,
  channel: string,
  body: TemplateSaveRequest,
  signal?: AbortSignal,
): Promise<TemplateView> {
  return apiRequest<TemplateView>(
    `/cabinet/notifications/templates/${encodeURIComponent(event)}/${encodeURIComponent(channel)}`,
    { method: "PUT", body, signal },
  )
}

export async function resetTemplate(
  event: string,
  channel: string,
  signal?: AbortSignal,
): Promise<TemplateView> {
  return apiRequest<TemplateView>(
    `/cabinet/notifications/templates/${encodeURIComponent(event)}/${encodeURIComponent(channel)}/reset`,
    { method: "POST", signal },
  )
}

export async function fetchTemplateVersions(
  templateId: string,
  signal?: AbortSignal,
): Promise<TemplateVersionsView> {
  return apiRequest<TemplateVersionsView>(
    `/cabinet/notifications/templates/${templateId}/versions`,
    { signal },
  )
}

export async function rollbackTemplate(
  templateId: string,
  version: number,
  signal?: AbortSignal,
): Promise<TemplateView> {
  return apiRequest<TemplateView>(`/cabinet/notifications/templates/${templateId}/rollback`, {
    method: "POST",
    body: { version },
    signal,
  })
}

/**
 * Предпросмотр набранного текста.
 *
 * POST, хотя ничего не меняет: тело шаблона — до четырёх тысяч знаков произвольного
 * текста, и в строке адреса ему не место ни по длине, ни потому, что адреса запросов
 * целиком попадают в журналы прокси.
 */
export async function previewTemplate(
  request: TemplatePreviewRequest,
  signal?: AbortSignal,
): Promise<TemplatePreviewView> {
  return apiRequest<TemplatePreviewView>("/cabinet/notifications/templates/preview", {
    method: "POST",
    body: request,
    signal,
  })
}

export async function testTemplate(
  request: TemplateTestRequest,
  signal?: AbortSignal,
): Promise<TemplateTestView> {
  return apiRequest<TemplateTestView>("/cabinet/notifications/templates/test", {
    method: "POST",
    body: request,
    signal,
  })
}

// ───────────────────────── матрица уведомлений (4-Б5) ─────────────────────────

/**
 * Экран «Каналы → Уведомления»: строки матрицы, состояние столбцов, тихие часы.
 *
 * ОДНИМ ЗАПРОСОМ, а не тремя. Состояние каналов без строк матрицы бессмысленно, а строки
 * без состояния каналов рисуются неправильно: клетка неподключённого канала обязана быть
 * неактивной с первого кадра, иначе салон успевает нажать и получить отказ.
 */
export async function fetchNotificationSettings(
  signal?: AbortSignal,
): Promise<NotificationSettingsView> {
  return apiRequest<NotificationSettingsView>("/cabinet/notifications/settings", { signal })
}

/** Правка одной строки: галочка события либо одна клетка канала. */
export async function updateNotificationRule(
  ruleId: string,
  request: UpdateNotificationRuleRequest,
): Promise<NotificationRuleView> {
  return apiRequest<NotificationRuleView>(`/cabinet/notifications/settings/rules/${ruleId}`, {
    method: "PUT",
    body: request,
  })
}

/** Тихие часы — одно значение на салон, сервер запишет его во все правила. */
export async function updateQuietHours(request: UpdateQuietHoursRequest): Promise<QuietHoursView> {
  return apiRequest<QuietHoursView>("/cabinet/notifications/settings/quiet-hours", {
    method: "PUT",
    body: request,
  })
}
